import type { DisplayFilter, FavoriteView, Scale, View, ViewConfig } from './types';
import { getFavoritePageType } from './types';
import { getRangeOptionsForView, type TimelineState } from './timelineUrl';
import { formatCalendarDate, getWeekEnd, getWeekNumber, getWeekStart } from './utils';

/**
 * Timeline on phones (plans/mobile-v2.md §1 D8, §3.6): only these views have a
 * mobile layout. The rest are desktop-only and show DesktopOnlyNotice on mobile.
 */
export const MOBILE_TIMELINE_VIEWS: View[] = ['days', 'focus', 'tasks'];

export function isMobileTimelineView(view: View): boolean {
  return MOBILE_TIMELINE_VIEWS.includes(view);
}

// ── Header title ────────────────────────────────────────────────────────────

export interface PeriodLabel {
  title: string;
  subtitle?: string;
}

const NBSP = ' ';

/**
 * Short period label for the mobile top bar (§3.3): "September" + "2026" at
 * month scale, "Sep 27 – Oct 3" + "W40" at week scale, "2026" at year scale.
 * Dates use a non-breaking space so a long title wraps at the dash, never
 * inside a date.
 */
export function getTimelinePeriodLabel(
  scale: Scale,
  currentMonth: string,
  currentWeek: string,
  currentYear: number,
): PeriodLabel {
  switch (scale) {
    case 'year':
      return { title: String(currentYear) };
    case 'week': {
      const fmt = (d: Date) =>
        `${formatCalendarDate(d, { month: 'short' })}${NBSP}${d.getUTCDate()}`;
      return {
        title: `${fmt(getWeekStart(currentWeek))} – ${fmt(getWeekEnd(currentWeek))}`,
        subtitle: `W${getWeekNumber(currentWeek)}`,
      };
    }
    default: {
      const [year, month] = currentMonth.split('-').map(Number);
      return {
        title: formatCalendarDate(new Date(Date.UTC(year, month - 1)), { month: 'long' }),
        subtitle: String(year),
      };
    }
  }
}

// ── View options sheet ──────────────────────────────────────────────────────

export const SCALE_LABELS: Record<Scale, string> = { month: 'Month', week: 'Week', year: 'Year' };

/** Compact range labels (segmented buttons on a phone). */
export const RANGE_LABELS: Record<string, string> = {
  '1-month': '1 Mo',
  quarter: 'Qtr',
  '3-months': '3 Mo',
  '6-months': '6 Mo',
  year: 'Year',
  '1-week': '1 Wk',
  month: 'Month',
  '5': '5 Wk',
  '7': '7 Wk',
  full: 'Full',
  half: 'Half',
};

export const DISPLAY_LABELS: Record<DisplayFilter, string> = {
  both: 'Both',
  projects: 'Projects',
  events: 'Events',
};

const SCALE_ORDER: Scale[] = ['month', 'week', 'year'];

/** Scales the view exists at (from `viewConfig.viewsByScale`), in desktop order. */
export function getScaleOptions(view: View, viewConfig: ViewConfig): Scale[] {
  return SCALE_ORDER.filter((s) => viewConfig.viewsByScale[s]?.includes(view));
}

/** Ranges the view offers at this scale; none when the view has no range control. */
export function getRangeOptions(view: View, scale: Scale, viewConfig: ViewConfig): string[] {
  if (!viewConfig.viewsWithRangeSupport?.includes(view)) return [];
  return getRangeOptionsForView(view, scale, viewConfig);
}

/** Display filters the view offers (`viewConfig.validDisplayOptions`). */
export function getDisplayOptions(view: View, viewConfig: ViewConfig): DisplayFilter[] {
  return (viewConfig.validDisplayOptions[view] ?? []) as DisplayFilter[];
}

/** Timeline favorites whose view has a mobile layout, in the user's order. */
export function getMobileFavorites(favorites: FavoriteView[]): FavoriteView[] {
  return favorites
    .filter(
      (f) => getFavoritePageType(f) === 'timeline' && !!f.view && isMobileTimelineView(f.view),
    )
    .sort((a, b) => a.position - b.position);
}

// The backend drops nil values, so compare null and undefined as equal.
const same = (a: unknown, b: unknown) => (a ?? null) === (b ?? null);

/** Whether a favorite is the configuration the Timeline is showing. */
export function isFavoriteCurrent(fav: FavoriteView, state: TimelineState): boolean {
  return (
    getFavoritePageType(fav) === 'timeline' &&
    fav.view === state.view &&
    fav.scale === state.scale &&
    same(fav.range, state.range) &&
    same(fav.display, state.display)
  );
}

/** Default name when saving the current configuration, e.g. "Week · Days". */
export function defaultFavoriteName(state: TimelineState): string {
  const viewLabel = state.viewConfig.viewLabels[state.view]?.label ?? state.view;
  const parts = [SCALE_LABELS[state.scale], viewLabel];
  // The server fills in a range even for views without a range control (Focus, Tasks).
  const hasRange = getRangeOptions(state.view, state.scale, state.viewConfig).length > 0;
  if (hasRange && state.range) parts.push(RANGE_LABELS[state.range] ?? String(state.range));
  return parts.join(' · ');
}
